import {
  useSession,
  useAsyncEffect,
  urls,
  IAccount,
  IPolicy,
  EAccountStatus,
} from '@jupiterone/jupiter-web-toolkit/src';
import React, { createContext, useContext, useState } from 'react';

export enum AccountType {
  ENTERPRISE = 'ENTERPRISE',
  PAID = 'PAID',
  FREE = 'FREE',
}

export interface Account extends IAccount {
  policy: IPolicy;
  type: AccountType;
}

export const useAccountsLogic = () => {
  const { accessToken, account: sessionAccount } = useSession();
  const [accounts, setAccounts] = useState<IAccount[]>();
  const [account, setAccount] = useState<Account>();
  // This is only true for the first load, updates should be false
  const [loading, setLoading] = useState<boolean>(true);

  useAsyncEffect(async () => {
    if (!accessToken || accounts) return;
    await refetchAccounts();
  }, [accessToken]);

  useAsyncEffect(async () => {
    if (!accounts) return; // Accounts not loaded yet
    if (!sessionAccount?.id || !accounts?.length || account) {
      setLoading(false);
      return;
    }
    await refetchAccount();
    setLoading(false);
  }, [sessionAccount?.id, accounts]);

  const refetchAccounts = async () => {
    if (!accessToken) return;

    const response = await fetch(urls.accounts, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const { accounts } = (await response.json()) as { accounts: IAccount[] };
    if (response.ok) {
      return setAccounts(
        accounts.filter(
          (a) => a.status === EAccountStatus.active && !a.deletionDate
        )
      );
    }
    // TODO: Report the error for this
    setAccounts([]);
  };

  const refetchAccount = async () => {
    if (!sessionAccount?.id || !accounts?.length) return;
    const url = urls.accountById.replace('${ACCOUNT_ID}', sessionAccount.id);
    const response = await fetch(url + '?include=policy', {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const account = await response.json();
    setAccount(account);
  };

  return {
    loading,
    accounts: accounts || [],
    account,
    isEnterprise: account?.type === AccountType.ENTERPRISE,
    refetchAccounts,
    refetchAccount,
  };
};

type UseAccounts = ReturnType<typeof useAccountsLogic>;
const AccountsContext = createContext({} as UseAccounts);
export const useAccounts = () => useContext(AccountsContext);

interface UseAccountsContextProviderProps {
  children: React.ReactNode;
}

export const AccountsProvider = ({
  children,
}: UseAccountsContextProviderProps) => {
  const accounts = useAccountsLogic();
  return (
    <AccountsContext.Provider value={accounts}>
      {children}
    </AccountsContext.Provider>
  );
};
